import { Router } from '@angular/router';
import { filter, takeUntil, skip, mergeMap, take, tap } from 'rxjs/operators';
import {
  Component,
  OnInit,
  OnDestroy,
  NgZone,
  AfterViewInit,
  ViewEncapsulation,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { setStorageDisplayView } from './core/store/actions/filter.actions';
import { NavigationBar } from '@hugotomazi/capacitor-navigation-bar';
import { SafeArea } from 'capacitor-plugin-safe-area';

import { AppVersionService } from './core/services/app-version.service';
import * as rootReducer from './core/store';
import * as fromAuthSelect from './core/store/selectors/auth.selectors';
import { PushService } from './core/services/push.service';
import { PlatformService } from './core/services/platform.service';
import { ConnectionService } from './core/services/connection.service';
import { refreshOfflineDelivery } from './core/store/actions/offline.actions';
import { RouterService } from './core/services/router.service';
import { ModalsService } from './shared/components/modals/modals.service';
import { Capacitor } from '@capacitor/core';

@Component({
  standalone: false,
  selector: 'pptl-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private platform: Platform,
    private store: Store<rootReducer.State>,
    private router: Router,
    private platformService: PlatformService,
    private pushService: PushService,
    private connectionService: ConnectionService,
    private ngZone: NgZone,
    private routerService: RouterService,
    private modalsService: ModalsService,
    private appVersionService: AppVersionService,
  ) {
    this.initializeApp();
    this.subscribeOnAuth();
    this.checkAppAvailability();
  }

  get isDesktop(): boolean {
    return this.platformService.isDesktop();
  }

  get isEdge(): boolean {
    return this.platformService.isEdge();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngOnInit() {
    this.routerService.setPreviousUrl();
    this.routerService.initGA();
    this.setDisplayView();
  }

  ngAfterViewInit(): void {
    if (!this.platformService.isCordova()) {
      return;
    }
    this.platform.ready().then(() => {
      SplashScreen.hide();
      this.addPaddingForNotch();
    });
  }

  initializeApp() {
    if (!this.platformService.isCordova()) {
      return;
    }
    this.appVersionService.init();
    this.platform.ready().then(() => {
      this.connectionService.initConnection();
      setTimeout(() => {
        if (this.platformService.isAndroid()) {
          NavigationBar?.setTransparency({ isTransparent: false });
        }
        this.addPaddingForNotch();
      }, 100);
      this.platform.resume.subscribe(() => this.listenDeeplinks());
      this.initAppData();
      this.initOfflineDeliveries();
    });
  }

  private initAppData(): void {
    this.listenDeeplinks();
  }

  private listenDeeplinks(): void {
    if (!this.platformService.isCordova()) {
      return;
    }
    App.addListener('appUrlOpen', ({ url }) => {
      const path = url ? new URL(url).pathname + new URL(url).search : null;
      this.ngZone.run(() => {
        this.navigateDelivery(path);
        this.navigateAuth(path, { $link: { path, queryString: new URL(url).search.slice(1) } });
      });
    });
  }

  private navigateDelivery(path) {
    if (path && path.includes('delivery')) {
      this.ngZone.run(() => {
        this.router.navigateByUrl(path);
      });
    }
  }

  private navigateAuth(path, error) {
    if (path && (path.includes('first-time-user-setup') || path.includes('reset-password'))) {
      const url = `${error?.$link?.path}?${error?.$link?.queryString}`;
      this.ngZone.run(() => {
        this.router.navigateByUrl(url);
      });
    }
  }

  private subscribeOnAuth() {
    this.store
      .pipe(
        select(fromAuthSelect.getAuthAccessToken),
        skip(1),
        tap((isLoggedIn) => {
          if (isLoggedIn) {
            this.pushService?.firebaseInit();
          }
        }),
        filter((isLoggedIn: string) => !isLoggedIn),
        mergeMap(() => this.store.pipe(select(rootReducer.getRouterState), take(1))),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.pushService?.unregister();
        this.goToLoginPage();
      });
  }

  private goToLoginPage() {
    this.router.navigate(['/auth/login']);
  }

  private checkAppAvailability() {
    this.modalsService.checkAppAvailability();
  }

  private initOfflineDeliveries() {
    const uuid = localStorage.getItem('uuid');
    if (uuid) {
      this.store.dispatch(refreshOfflineDelivery());
    }
  }

  private setDisplayView() {
    this.store.dispatch(setStorageDisplayView());
  }

  private async addPaddingForNotch(): Promise<void> {
    if (this.platformService.isAndroid() && Capacitor) {
      SafeArea.getSafeAreaInsets().then(({ insets }) => {
        for (const [key, value] of Object.entries(insets)) {
          document.documentElement.style.setProperty(`padding-${key}`, `${value}px`);
          document.documentElement.style.setProperty(`--help-button-padding-${key}`, `${value}px`);
          //document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${value}px`);
        }
      });

      // when safe-area changed
      const eventListener = await SafeArea.addListener('safeAreaChanged', (data) => {
        const { insets } = data;
        for (const [key, value] of Object.entries(insets)) {
          document.documentElement.style.setProperty(`padding-${key}`, `${value}px`);
          document.documentElement.style.setProperty(`--help-button-padding-${key}`, `${value}px`);
          //document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${value}px`);
        }
      });

      eventListener.remove();
    }
  }
}
